import { Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetencySettingsPageDocument,
  CompetenciesEditBasicInformationDocument,
  CompetencySettingsInfoFragment,
} from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CompetencyGroupSelector } from 'modules/competencies/components/CompetencyGroupSelector'

interface CompetencyFormValues {
  name: string
  description: string
  competencyGroupId?: string
}

interface Props {
  competency: CompetencySettingsInfoFragment
}

const valuesFromCompetency = (
  competency?: CompetencySettingsInfoFragment | null
): CompetencyFormValues => ({
  name: competency?.name || '',
  description: competency?.description || '',
  competencyGroupId: competency?.competencyGroup?.id,
})

export const BasicInformationSection = ({ competency }: Props) => {
  const [editBasicInformation] = useMutation(CompetenciesEditBasicInformationDocument, {
    extractorFn: (data) => data.competenciesMutations.updateCompetenciesCompetency,
    refetchQueries: [CompetenciesCompetencySettingsPageDocument],
  })

  const form = useForm<CompetencyFormValues>({
    defaultValues: valuesFromCompetency(competency),
    validate: (data) => {
      const errors: FormErrors<CompetencyFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(data, { setErrors }) {
      const result = await editBasicInformation({
        variables: {
          id: competency.id,
          name: data.name,
          description: data.description,
          competencyGroupId: data.competencyGroupId,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('competencies_v3.settings.update_success'),
        })
        form.resetForm({ values: valuesFromCompetency(handled.data.competency) })
        track(trackingEvents.competencies.editCompetency)
      }

      return handled
    },
  })

  return (
    <Box gap="s24">
      <Text.Header2>{i18n.t('competencies_v3.settings.basic_information.title')}</Text.Header2>
      <Form onSubmit={form.handleSubmit} padding={null}>
        <TextField
          label={i18n.t('competencies_v3.settings.basic_information.name_input')}
          value={form.values.name}
          onChange={(value) => form.setFieldValue('name', value)}
          error={form.touched.name ? form.errors.name : undefined}
          mandatory
        />
        <TextArea
          label={i18n.t('competencies_v3.settings.basic_information.description_input')}
          value={form.values.description}
          onChange={(value) => form.setFieldValue('description', value)}
          error={form.touched.description ? form.errors.description : undefined}
        />
        <CompetencyGroupSelector
          value={form.values.competencyGroupId}
          defaultCompetencyGroup={competency.competencyGroup}
          onChange={(groupId) => form.setFieldValue('competencyGroupId', groupId ?? undefined)}
          error={form.touched.competencyGroupId ? form.errors.competencyGroupId : undefined}
        />
        <Button submit>{i18n.t('common.save')}</Button>
      </Form>
    </Box>
  )
}
